import { Segmented } from "antd"
import React, { useCallback, useEffect, useRef, useState } from "react"

export type ProposalsViewSection =
  | "document"
  | "versions"
  // | "comments"
  | "container"

const sections: { label: string; value: ProposalsViewSection }[] = [
  { label: "Document", value: "document" },
  { label: "Versions", value: "versions" }
  // { label: "Comments", value: "comments" }
]

interface ProposalsViewScrollWatcherProps {
  refsMap: Record<ProposalsViewSection, React.RefObject<HTMLDivElement | null>>
}

export function ProposalsViewScrollTabs({
  refsMap
}: ProposalsViewScrollWatcherProps) {
  const [activeSection, setActiveSection] =
    useState<ProposalsViewSection>("document")
  const isClickScrolling = useRef(false)

  const containerRef = refsMap?.container

  const getVisiblePercentage = (
    element: HTMLElement,
    container: HTMLElement
  ) => {
    const containerRect = container.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()

    const visibleTop = Math.max(elementRect.top, containerRect.top)
    const visibleBottom = Math.min(elementRect.bottom, containerRect.bottom)
    const visibleHeight = Math.max(0, visibleBottom - visibleTop)

    return visibleHeight / elementRect.height
  }

  const handleScroll = useCallback(() => {
    if (isClickScrolling.current) return

    const container = containerRef.current
    if (!container) return

    let maxVisibility = 0
    let mostVisibleSection: ProposalsViewSection = "document"

    for (const { value } of sections) {
      const el = refsMap[value].current
      if (el) {
        const visibility = getVisiblePercentage(el, container)

        // If the section is 75%+ visible, we immediately select it
        if (visibility >= 0.75) {
          setActiveSection(value)
          return
        }

        // otherwise we remember the most visible
        if (visibility > maxVisibility) {
          maxVisibility = visibility
          mostVisibleSection = value
        }
      }
    }

    setActiveSection(mostVisibleSection)
  }, [])

  const onScrollToSection = (section: ProposalsViewSection) => {
    isClickScrolling.current = true
    setActiveSection(section)

    refsMap[section].current?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    })

    setTimeout(() => {
      isClickScrolling.current = false
    }, 500)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener("scroll", handleScroll)
    return () => container.removeEventListener("scroll", handleScroll)
  }, [handleScroll])

  return (
    <Segmented
      value={activeSection}
      options={sections}
      className={"w-fit self-end"}
      onChange={(value) => onScrollToSection(value as ProposalsViewSection)}
    />
  )
}
